/**
 * Directors' Room (Sep 2026): the directors' meeting (every second Tuesday since 10 Nov 2026). Running agenda,
 * notes, decisions and actions, replacing the "NEW DirectorMeetingAgenda"
 * Google Sheet. Access = `tools.directors.enabled`, nothing else (no admin
 * override: the content is confidential to the directors).
 *
 * Collections (server-only, rules deny all client access):
 *   /directorTopics/{id}     one agenda topic
 *   /directorActions/{id}    one action ("who does what next")
 *   /directorMeetings/{date} a CLOSED meeting (date = ISO YYYY-MM-DD)
 *   /directorCompanyMeetings/{date}  a company team meeting session (Oct 2026)
 *   /directorCompanyItems/{id}       one agenda row of a company team meeting
 *
 * Dates are ISO YYYY-MM-DD strings; created/updated stamps are ISO datetimes.
 * People are lowercase emails; the client maps them to names via `people`.
 */
import { z } from 'zod';
/** What the owner needs from the room. FYI topics take no meeting time. */
export const DIRECTOR_TOPIC_KINDS = ['decision', 'input', 'update', 'fyi'];
export const DIRECTOR_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
export const DIRECTOR_ATTACHMENTS_PER_TOPIC = 10;
/** Storage path of a topic attachment (storage.rules mirrors it). */
export function directorAttachmentPath(topicId, attachmentId) {
    return `directorAttachments/${topicId}/${attachmentId}`;
}
/** True when `email` is tagged on an open topic and hasn't marked their input done. */
export function directorInputPending(topic, email) {
    return topic.status === 'new'
        && (topic.requiredEmails ?? []).includes(email)
        && !(topic.requiredDoneEmails ?? []).includes(email);
}
/** Owner of a group action ("[The group]" in Gemini notes). */
export const DIRECTOR_ALL = 'all';
/** Whose Drive holds the recordings (the organizer of the recurring invite) and the event title. */
export const DIRECTORS_MEDIA_ORGANIZER = 'michael.leitner@daaily.com';
export const DIRECTORS_MEDIA_TITLE = 'Director Meeting';
/** Display name of a topic/action/item owner: a director's name, 'All', or the imported free-text owner. */
export function directorOwnerLabel(row, nameOf) {
    if (row.ownerEmail)
        return nameOf(row.ownerEmail);
    return row.ownerName?.trim() || '-';
}
/** Filter key for an owner: the email, or `name:<ownerName>` for an imported free-text owner. */
export function directorOwnerKey(row) {
    if (row.ownerEmail)
        return row.ownerEmail;
    return row.ownerName?.trim() ? `name:${row.ownerName.trim()}` : '';
}
/** Name of a copied deck: "Company Team Meeting – 05.11.2026". */
export function companyDeckName(date) {
    const [y, m, d] = date.split('-');
    return `Company Team Meeting \u2013 ${d}.${m}.${y}`;
}
/** The Slides file id inside a docs.google.com/presentation link, or null. */
export function slidesFileIdFromUrl(url) {
    return /^https:\/\/docs\.google\.com\/presentation\/(?:u\/\d+\/)?d\/([a-zA-Z0-9_-]+)/.exec(url.trim())?.[1] ?? null;
}
export function slidesUrlOf(fileId) {
    return `https://docs.google.com/presentation/d/${fileId}/edit`;
}
/** The meeting day: Tuesday (0 = Sunday). */
export const DIRECTORS_MEETING_WEEKDAY = 2;
/**
 * Every second Tuesday (since 10 Nov 2026; weekly before). The anchor is any meeting on the
 * grid: a Tuesday counts when its distance to the anchor is a whole number of cycles.
 */
export const DIRECTORS_MEETING_EVERY_DAYS = 14;
export const DIRECTORS_MEETING_ANCHOR = '2026-11-10';
export const DIRECTORS_MEETING_MINUTES = 60;
function addDays(iso, n) {
    const d = new Date(`${iso}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}
function dayNumber(iso) {
    return Math.round(new Date(`${iso}T12:00:00Z`).getTime() / 86_400_000);
}
/** Whether `iso` is a directors' meeting day on the biweekly Tuesday grid. */
export function isDirectorMeetingDay(iso) {
    if (new Date(`${iso}T12:00:00Z`).getUTCDay() !== DIRECTORS_MEETING_WEEKDAY)
        return false;
    const diff = dayNumber(iso) - dayNumber(DIRECTORS_MEETING_ANCHOR);
    return ((diff % DIRECTORS_MEETING_EVERY_DAYS) + DIRECTORS_MEETING_EVERY_DAYS) % DIRECTORS_MEETING_EVERY_DAYS === 0;
}
/**
 * Open meetings, soonest first: any earlier meeting date that still has open
 * topics and was never closed (a missed close must not hide its agenda), then
 * the next `count` meeting days from today that aren't closed.
 */
export function upcomingDirectorMeetings(today, closedDates, openTopicDates, count = 3) {
    const closed = new Set(closedDates);
    const out = new Set();
    for (const d of openTopicDates)
        if (d < today && !closed.has(d))
            out.add(d);
    let d = today;
    while (!isDirectorMeetingDay(d))
        d = addDays(d, 1);
    let added = 0;
    while (added < count) {
        if (!closed.has(d)) {
            out.add(d);
            added++;
        }
        d = addDays(d, DIRECTORS_MEETING_EVERY_DAYS);
    }
    // Topics filed on a non-standard date (moved meeting) still show.
    for (const x of openTopicDates)
        if (x >= today && !closed.has(x))
            out.add(x);
    return [...out].sort();
}
/**
 * Where a carried topic goes: the first meeting day after `date` that is also
 * today or later and not closed. A late close (closing Sep 22 on Sep 30) must
 * not carry into a meeting that is already past.
 */
export function nextDirectorMeeting(date, closedDates, today) {
    const closed = new Set(closedDates);
    let d = addDays(date > today ? date : today, date >= today ? 1 : 0);
    while (!isDirectorMeetingDay(d) || closed.has(d))
        d = addDays(d, 1);
    return d;
}
// ── Callable inputs ─────────────────────────────────────────────────────────
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const email = z.string().trim().toLowerCase().max(200);
const ownerOrAll = z.union([email.email(), z.literal(DIRECTOR_ALL)]);
/** Only Google Docs links: they are rendered as <a href>, so no other scheme (javascript:) may get in. */
const googleDocUrl = z.string().trim().max(600).regex(/^https:\/\/docs\.google\.com\//, 'Must be a Google Docs link.');
const transcript = z.array(z.object({ at: z.string().regex(/^\d{2}:\d{2}:\d{2}$/), href: googleDocUrl })).max(20);
export const DirectorsSaveTopicSchema = z.object({
    /** Omit to create; the caller becomes the owner. */
    id: z.string().trim().min(1).max(80).optional(),
    meetingDate: isoDate,
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).nullish(),
    kind: z.enum(DIRECTOR_TOPIC_KINDS),
    minutes: z.number().int().min(0).max(120),
    file: z.string().trim().max(600).nullish(),
    /** Directors whose input is required (the server keeps only Directors' Room members). */
    requiredEmails: z.array(email.email()).max(10).optional(),
});
/** Live capture during the meeting. */
export const DirectorsUpdateTopicSchema = z.object({
    id: z.string().trim().min(1).max(80),
    notes: z.string().max(20_000).nullish(),
    decision: z.string().max(2000).nullish(),
    status: z.enum(['new', 'discussed']).optional(),
    /** The CALLER marks their required input done (true) or not (false). */
    inputDone: z.boolean().optional(),
});
export const DirectorsCarryTopicSchema = z.object({ id: z.string().trim().min(1).max(80) });
/** Delete an open topic (and its actions) while its meeting is open. */
export const DirectorsDeleteTopicSchema = z.object({ id: z.string().trim().min(1).max(80) });
const attachmentId = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/, 'Invalid attachment id.');
/** Record a file the client just uploaded to `directorAttachmentPath(topicId, id)`. */
export const DirectorsAddAttachmentSchema = z.object({
    topicId: z.string().trim().min(1).max(80),
    id: attachmentId,
    name: z.string().trim().min(1).max(200),
});
/** View/download link (`download` → attachment disposition) or delete. */
export const DirectorsAttachmentRefSchema = z.object({
    topicId: z.string().trim().min(1).max(80),
    id: attachmentId,
    download: z.boolean().optional(),
});
export const DirectorsAddActionSchema = z.object({
    topicId: z.string().trim().max(80).nullish(),
    meetingDate: isoDate,
    topicTitle: z.string().trim().max(200),
    ownerEmail: ownerOrAll,
    text: z.string().trim().min(1).max(500),
    due: z.union([isoDate, z.literal('')]),
});
export const DirectorsSetActionDoneSchema = z.object({ id: z.string().trim().min(1).max(80), done: z.boolean() });
export const DirectorsImportNotesSchema = z.object({
    meetingDate: isoDate,
    docUrl: googleDocUrl.nullish(),
    text: z.string().max(200_000).nullish(),
});
export const DirectorsCloseMeetingSchema = z.object({
    date: isoDate,
    attendeeEmails: z.array(email.email()).max(50),
    notesUrl: googleDocUrl.nullish(),
    agenda: z.array(z.object({
        topicId: z.string().trim().min(1).max(80),
        discussed: z.boolean(),
        // Live notes (≤20k) plus the appended Gemini summary (≤4k).
        notes: z.string().max(25_000).nullish(),
        decision: z.string().max(2000).nullish(),
        transcript: transcript.nullish(),
    })).max(100),
    raised: z.array(z.object({
        title: z.string().trim().min(1).max(200),
        ownerEmail: email.email(),
        notes: z.string().max(20_000),
        decision: z.string().max(2000).nullish(),
        transcript: transcript.nullish(),
    })).max(50),
    /** topicRef = an agenda topic id, or `raised:<index>` into `raised`. */
    actions: z.array(z.object({
        topicRef: z.string().trim().min(1).max(90),
        ownerEmail: ownerOrAll,
        text: z.string().trim().min(1).max(500),
        due: z.union([isoDate, z.literal('')]),
    })).max(100),
});
// ── Company team meeting inputs ─────────────────────────────────────────────
/** Only Google Slides links: rendered as <a href>. */
const slidesUrl = z.string().trim().max(600).regex(/^https:\/\/docs\.google\.com\/presentation\//, 'Must be a Google Slides link (https://docs.google.com/presentation/…).');
/** Create a session (`create: true`, refused when the date exists) or update one. */
export const DirectorsSaveCompanyMeetingSchema = z.object({
    date: isoDate,
    create: z.boolean().optional(),
    /** null clears the deck link. */
    deckUrl: slidesUrl.nullish(),
    status: z.enum(['planned', 'done']).optional(),
});
/**
 * Create (no id) or patch an item. On a patch only the fields sent change, so
 * an edit never wipes an imported ownerName unless the owner itself is changed.
 */
export const DirectorsSaveCompanyItemSchema = z.object({
    id: z.string().trim().min(1).max(80).optional(),
    meetingDate: isoDate,
    title: z.string().trim().min(1).max(300).optional(),
    presenter: z.string().trim().max(200).optional(),
    /** A director's email or ''. */
    ownerEmail: z.union([email.email(), z.literal('')]).optional(),
    /** Free-text owner; only kept when ownerEmail is ''. */
    ownerName: z.string().trim().max(120).nullish(),
    minutes: z.number().int().min(0).max(600).nullish(),
    notes: z.string().max(5000).optional(),
    /** Standing item: copied into the next session when it is created. */
    standing: z.boolean().optional(),
});
export const DirectorsDeleteCompanyItemSchema = z.object({ id: z.string().trim().min(1).max(80) });
/** The full new order of a session's items (every item id of that session, once). */
export const DirectorsReorderCompanyItemsSchema = z.object({
    meetingDate: isoDate,
    ids: z.array(z.string().trim().min(1).max(80)).max(200),
});
/** Copy the most recent earlier session's deck for this session. */
export const DirectorsCreateDeckSchema = z.object({
    date: isoDate,
    /** Replace a deck the session already has. */
    replace: z.boolean().optional(),
});
//# sourceMappingURL=directors.js.map